import { useEffect, useState } from 'react';
import type { NotificationItem, NotificationsFeed } from '../services/authApi';
import { fetchActiveResearchJobs } from '../services/activeResult';
import { parseJobProgress, progressLine } from '../services/watchedJobs';
import { useAuth } from '../contexts/AuthContext';

type Tab = 'all' | 'running' | 'search' | 'label';
type RunningJobs = Record<string, unknown>[];

interface Props {
  feed: NotificationsFeed | null;
  error: boolean;
  onRetry: () => void;
  onItemAction: (item: NotificationItem, path: string) => void;
  onMarkAllRead: () => Promise<boolean>;
  onNavigate: (path: string) => void;
  initialRunning: RunningJobs | null;
  onRunningLoaded: (jobs: RunningJobs) => void;
}

const MONTH_DAY: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };

function whenLabel(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (d.toDateString() === now.toDateString()) return 'Today';
  const opts: Intl.DateTimeFormatOptions =
    d.getFullYear() === now.getFullYear() ? MONTH_DAY : { ...MONTH_DAY, year: 'numeric' };
  return d.toLocaleDateString('en-US', opts);
}

/** `YYYY-MM-DD...` -> "MMM d, yyyy" without a timezone shift. */
function versionDate(s: string): string {
  const [y = 0, m = 1, d = 1] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { ...MONTH_DAY, year: 'numeric' });
}

function searchSub(item: NotificationItem): string {
  const regions = [...new Set((item.matches ?? []).map((m) => m.region).filter(Boolean))];
  return ['Keyword · Labels', ...(regions.length ? [regions.join(', ')] : [])].join(' · ');
}

function labelSub(item: NotificationItem): string {
  const to = item.version_label ?? `v${item.to_version}`;
  const from = item.from_version_label ?? (item.from_version != null ? `v${item.from_version}` : null);
  const parts = [from != null ? `${from} → ${to}` : to];
  if (item.version_date) parts.push(versionDate(item.version_date));
  if (item.region) parts.push(item.region);
  return parts.join(' · ');
}

const linkCls = 'text-accent hover:underline text-left';

function ItemRow({ item, onItemAction }: {
  item: NotificationItem;
  onItemAction: Props['onItemAction'];
}) {
  const [expanded, setExpanded] = useState(false);
  const isSearch = item.kind === 'search';
  const matches = item.matches ?? [];
  const shown = expanded ? matches : matches.slice(0, 5);
  const n = matches.length;
  return (
    <li className="px-4 py-3 border-b border-line last:border-b-0" data-testid={`notif-${item.key}`}>
      <div className="flex items-center gap-2 font-mono text-[11px] tracking-wide">
        {!item.read && (
          <span data-testid="unread-marker" className="inline-block w-1.5 h-1.5 rounded-full bg-accent" />
        )}
        <span className={isSearch ? 'text-accent' : 'text-ink-3'}>
          {isSearch ? 'SAVED SEARCH' : 'WATCHLIST'}
        </span>
        <span className="text-ink-3">{whenLabel(item.created_at)}</span>
      </div>
      <div className={`mt-1 text-sm text-ink ${item.read ? '' : 'font-semibold'}`}>
        {isSearch
          ? `${n} label update${n === 1 ? '' : 's'} match "${item.subscription_name}"`
          : `${item.display_name} label updated`}
      </div>
      <div className="text-xs text-ink-3">
        {isSearch ? searchSub(item) : labelSub(item)}
      </div>
      {isSearch ? (
        <div className="mt-1 flex flex-col items-start gap-0.5 text-xs">
          {shown.map((m) => (
            <button key={m.drug_id} type="button" className={linkCls}
              onClick={() => onItemAction(item, `/drug/${m.drug_id}`)}>
              {m.display_name}
            </button>
          ))}
          {!expanded && n > 5 && (
            <button type="button" className="text-ink-3 hover:underline"
              onClick={() => setExpanded(true)}>
              {`+${n - 5} more`}
            </button>
          )}
        </div>
      ) : (
        <button type="button" className={`mt-1 text-xs ${linkCls}`}
          onClick={() => onItemAction(item, `/drug/${item.drug_id}`)}>
          Open product
        </button>
      )}
    </li>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title}>
      <h3 className="px-4 pt-3 pb-1 font-mono text-[11px] tracking-wide text-ink-3">{title}</h3>
      {children}
    </section>
  );
}

export default function NotificationsPanel({
  feed, error, onRetry, onItemAction, onMarkAllRead, onNavigate, initialRunning, onRunningLoaded,
}: Props) {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('all');
  const [running, setRunning] = useState<RunningJobs | 'error' | null>(initialRunning);
  const [markFailed, setMarkFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetchActiveResearchJobs()
      .then((jobs) => {
        if (!live) return;
        setRunning(jobs as RunningJobs);
        onRunningLoaded(jobs as RunningJobs);
      })
      .catch(() => { if (live) setRunning('error'); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch once per open
  }, []);

  const runningCount = Array.isArray(running) ? running.length : 0;
  const unread = feed?.unread_count ?? 0;

  const markAll = async () => {
    setMarkFailed(false);
    if (!(await onMarkAllRead())) setMarkFailed(true);
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'running', label: runningCount > 0 ? `Running · ${runningCount}` : 'Running' },
    { id: 'search', label: 'Saved searches' },
    { id: 'label', label: 'Watchlist' },
  ];

  const showRunning = tab === 'all' || tab === 'running';
  const showFeed = tab !== 'running';
  const kindItems = (feed?.items ?? []).filter(
    (i) => tab === 'all' || i.kind === tab,
  );
  const newItems = kindItems.filter((i) => !i.read);
  const earlier = kindItems.filter((i) => i.read);

  const runningSection = showRunning && (running === 'error' ? (
    <Section title="RUNNING NOW">
      <p className="px-4 py-2 text-sm text-ink-3">Couldn't load running jobs.</p>
    </Section>
  ) : runningCount > 0 ? (
    <Section title="RUNNING NOW">
      <ul>
        {(running as RunningJobs).map((job) => {
          const p = parseJobProgress(job);
          return (
            <li key={String(job['id'])} className="px-4 py-3 border-b border-line">
              <div className="font-mono text-[11px] tracking-wide text-accent">INVESTIGATOR</div>
              <div className="mt-1 text-sm text-ink line-clamp-2">{String(job['query'] ?? '')}</div>
              <div className="text-xs text-ink-3">{p ? progressLine(p) : 'Working…'}</div>
              <button type="button" className={`mt-1 text-xs ${linkCls}`}
                onClick={() => onNavigate(`/research/${job['id']}`)}>
                Go to tab
              </button>
            </li>
          );
        })}
      </ul>
    </Section>
  ) : null);

  const emptyCopy = (): React.ReactNode => {
    if (tab === 'running') return <p className="px-4 py-6 text-sm text-ink-3">Nothing running.</p>;
    if (tab === 'search') return <p className="px-4 py-6 text-sm text-ink-3">No saved-search alerts in the last 30 days.</p>;
    if (tab === 'label') return <p className="px-4 py-6 text-sm text-ink-3">No watched-label alerts in the last 30 days.</p>;
    return (
      <div className="px-4 py-6 text-sm text-ink-3">
        <p className="text-ink">No notifications yet.</p>
        <p className="mt-1">
          Save a keyword search or watch a label to get alerts here.
        </p>
        <button type="button" className={`mt-2 ${linkCls}`} onClick={() => onNavigate('/account')}>
          Manage alerts
        </button>
      </div>
    );
  };

  let body: React.ReactNode;
  if (!feed && !error) {
    body = <p role="status" className="px-4 py-6 text-sm text-ink-3">Loading…</p>;
  } else if (!feed) {
    body = (
      <div className="px-4 py-6 text-sm text-ink-3">
        <p>Couldn't load notifications.</p>
        <button type="button" className={`mt-2 ${linkCls}`} onClick={onRetry}>Retry</button>
      </div>
    );
  } else {
    const hasRunning = showRunning && (runningCount > 0 || running === 'error');
    const hasFeed = showFeed && kindItems.length > 0;
    body = (
      <>
        {runningSection}
        {showFeed && newItems.length > 0 && (
          <Section title="NEW">
            <ul>{newItems.map((i) => <ItemRow key={i.key} item={i} onItemAction={onItemAction} />)}</ul>
          </Section>
        )}
        {showFeed && earlier.length > 0 && (
          <Section title="EARLIER">
            <ul>{earlier.map((i) => <ItemRow key={i.key} item={i} onItemAction={onItemAction} />)}</ul>
          </Section>
        )}
        {!hasRunning && !hasFeed && !(tab === 'running' && running === null) && emptyCopy()}
        {feed.has_more && (
          <p className="px-4 py-2 text-xs text-ink-3">Showing the latest 100 notifications.</p>
        )}
      </>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Notifications"
      className="fixed left-4 right-4 top-14 z-50 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[480px] max-h-[70vh] overflow-auto rounded-lg border border-line bg-white"
      style={{ boxShadow: '0 12px 32px rgba(19,28,25,.14)' }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-line">
        <h2 className="text-sm font-semibold text-ink">Notifications</h2>
        <div className="flex items-center gap-3 text-xs">
          <button type="button" className={`${linkCls} disabled:text-ink-3 disabled:no-underline disabled:cursor-not-allowed`}
            disabled={unread === 0} onClick={markAll}>
            Mark all read
          </button>
          <button type="button" className={linkCls} onClick={() => onNavigate('/account')}>
            Settings
          </button>
        </div>
      </div>
      {markFailed && (
        <p className="px-4 py-2 text-xs text-signal">Couldn't mark as read. Try again.</p>
      )}
      <div className="flex gap-4 px-4 border-b border-line text-sm">
        {tabs.map((t) => (
          <button key={t.id} type="button" aria-pressed={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`py-2 border-b-2 ${tab === t.id ? 'border-accent text-ink' : 'border-transparent text-ink-3'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {body}
      {feed?.emails_enabled && user?.email && (
        <p className="px-4 py-3 border-t border-line text-xs text-ink-3">
          {`Also emailed to ${user.email} · `}
          <button type="button" className={linkCls} onClick={() => onNavigate('/account')}>
            Change in your account
          </button>
        </p>
      )}
    </div>
  );
}
